import * as React from 'react';
import { gsap } from 'gsap';

import { useMouseTracker } from './use-mouse-tracker';

export const useLantern = <T extends HTMLElement>({
  size,
  isFocused
}: {
  size: 'sm' | 'md' | 'lg';
  isFocused?: boolean;
}) => {
  const maskRef = React.useRef<T>(null);
  const [isFocusing, setIsFocusing] = React.useState(isFocused ?? false);
  const [hasRendered, setHasRendered] = React.useState(false);

  React.useEffect(() => {
    setHasRendered(true);
  }, []);

  const sizes = React.useMemo(() => {
    if (size === 'sm') {
      return {
        '--radius-x': isFocusing ? '190%' : '30px',
        '--radius-y': isFocusing ? '190%' : '30px',
        '--opacity': isFocusing ? 1 : 0
      };
    }

    if (size === 'md') {
      return {
        '--radius-x': isFocusing ? '290%' : '180px',
        '--radius-y': isFocusing ? '290%' : '180px',
        '--opacity': isFocusing ? 1 : 0
      };
    }

    if (size === 'lg') {
      return {
        '--radius-x': isFocusing ? '390%' : '230px',
        '--radius-y': isFocusing ? '390%' : '230px',
        '--opacity': isFocusing ? 1 : 0
      };
    }
  }, [size, isFocusing]);

  const { elementRef } = useMouseTracker(
    React.useMemo(() => {
      return {
        onChange: ({ x, y, isHovering, first }) => {
          if (!maskRef.current) return;
          if (isFocusing) return;
          const webkitMaskImage = `radial-gradient(var(--radius-x) var(--radius-y) at ${x}px ${y}px, black 45%, transparent)`;
          if (first) {
            gsap.set(maskRef.current, {
              webkitMaskImage,
              filter: 'contrast(170%)',
              brightness: '1000%'
            });
            gsap.to(maskRef.current, {
              ...sizes,
              '--opacity': isHovering ? 1 : 0,
              duration: 0.5,
              ease: 'power3.easeOut'
            });
          } else {
            gsap.to(maskRef.current, {
              ...sizes,
              '--opacity': isHovering ? 1 : 0,
              webkitMaskImage,
              duration: 0.5,
              ease: 'power3.easeOut'
            });
          }
        },
        windowAsProxy: true
        // enableOnlyWhenHovering: true,
      };
    }, [isFocusing, sizes])
  );

  React.useEffect(() => {
    if (!maskRef.current) return;
    const tween = gsap.to(maskRef.current, {
      ...sizes
    });

    return () => {
      tween.kill();
    };
  }, [sizes]);

  return {
    maskRef,
    elementRef,
    focusHandlers: {
      onFocus: () => setIsFocusing(true),
      onBlur: () => setIsFocusing(false)
    }
  };
};
